import React, {
  memo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { InterviewSchedulerForm } from './InterviewSchedulerForm';
import {
  InterviewManualScheduler,
  InterviewSelfScheduler,
} from './InterviewCalendar';
import { SchedulerDetailsConstants } from './SchedulerDetails.constants';
import { useSharedData } from '@/hooks/useSharedData';
import { v4 as uuidv4 } from 'uuid';
import { useDateFormat } from '@/hooks';
import { KText } from '@/ui-library/components';
import { SchedulerDetailsTypes } from './SchedulerDetails.types';
import moment from 'moment-timezone';
import { schedulerDetailsUtils } from './utils/schedulerDetails.utils';
import { useSchedulerDetailsPayload } from './hooks/useSchedulerDetailsPayload';
import { SchedulerDetailsFooter } from './SchedulerDetailsFooter';
import { useAuth } from '@/hooks/use-auth';
import { createPreviewDetails } from '../Scheduler';

export const SchedulerDetails = memo(
  forwardRef((props: SchedulerDetailsTypes.SchedulerDetailsProps, ref) => {
    const {
      interviewPlan,
      jobId,
      jobStageActivityId,
      variant = 'manual',
      isEdit = false,
      isAdhoc = false,
      candidate = {},
      contacts = [],
      interviewConfigs,
      currentInterviewIndex,
      previewDetails,
      updateInterviewDataCallback,
      isBulkManual = false,
      persistedUserEventsInfo,
      persistedRoomEventsInfo,
      bulkBookedEvents = [],
      navigationConfig,
      applicationData,
      timezone,
      currentTemplateData,
      updateTemplateModificationData,
      isManualScheduling = false,
      customForm = null,
      interviewPlatform = null,
      timezoneModalTrigger = 0,
      showScorecardStep = false,
      formOnly = false,
      candidateAvailabilitySlots,
    } = props as any;

    const { currUser } = useAuth();
    const { dateFormat } = useDateFormat();

    // Reuse the exported function from Scheduler.tsx
    const getPreviewDetails = useCallback(() => {
      return {
        candidate: createPreviewDetails({
          previewDetails,
          payload: null, // No payload in bulk manual mode
          dateFormat,
          interviewPlan, // Pass interviewPlan for bulk manual mode
          timezone, // Pass timezone for bulk manual mode
          isInterviewer: false,
        }),
        interviewer: createPreviewDetails({
          previewDetails,
          payload: null, // No payload in bulk manual mode
          dateFormat,
          interviewPlan, // Pass interviewPlan for bulk manual mode
          timezone, // Pass timezone for bulk manual mode
          isInterviewer: true,
        }),
      };
    }, [previewDetails, dateFormat, interviewPlan, timezone]);

    const previewPayload = useMemo(() => {
      const payload = {
        contact_id: applicationData?.contact?.id,
        ats_job_id: applicationData?.job_id || jobId,
        sender_id: currUser?.id,
        ats_job_stage_id: applicationData?.job_stage_id || jobStageActivityId,
      };

      return payload;
    }, [applicationData, jobId, currUser?.id, jobStageActivityId]);

    const { initializeSelfScheduleTimeSlots, getDurationFromTemplate } =
      schedulerDetailsUtils ?? {};

    const {
      dataRef: interviewDataRef,
      updateData: updateSharedData,
      registerForUpdates,
    } = useSharedData<SchedulerDetailsTypes.InterviewData>({
      interviewers: [],
      meetingRooms: [],
      meetingRoomObj: {},
      interviewName: '',
      interviewKind: undefined,
      bookedSlot: {
        ...SchedulerDetailsConstants.BOOKED_EVENT_PROPS,
        id: uuidv4(),
      },
      datesSet: undefined,
      currentWeek: '',
      colorCodes: [],
      userCalendarInfo: {},
      roomCalendarInfo: [],
      findTimeValue: '',
      resetAllSlots: false,
      schedulerState: {},
      candidate,
      interviewersDetails: {},
      userEventsInfo: persistedUserEventsInfo || {},
      roomEventsInfo: persistedRoomEventsInfo || {},
      bulkBookedEvents,
      calendar_visibility: interviewPlan?.calendar_visibility || 'default',
      ...(interviewPlan?.shared_calendar_enabled
        ? { calendar_info: interviewPlan?.calendar_info ?? {} }
        : {}),
      minimum_notice_config: interviewPlan?.minimum_notice_config,
      minimum_notice_enabled: interviewPlan?.minimum_notice_enabled,
      fill_with_ai_enabled: interviewPlan?.fill_with_ai_enabled,
      candidateAvailabilitySlots,
    } as SchedulerDetailsTypes.InterviewData);

    const calendarRef = useRef<any>(null);
    const formRef = useRef<any>(null);

    useEffect(() => {
      if (interviewDataRef?.current) {
        interviewDataRef.current.timezoneModalTrigger = timezoneModalTrigger;
      }
    }, [timezoneModalTrigger, interviewDataRef]);

    const { getSchedularDetailsModel } = useSchedulerDetailsPayload({
      contacts,
      formRef,
      formOnly,
      interviewDataRef,
      interviewPlan,
      isEdit,
      variant,
      previewDetailsRef: previewDetails,
    });

    // When formOnly (no calendar), set datesSet once so interviewer fetch has week_start_date (next 2 weeks from today)
    useEffect(() => {
      if (!formOnly || !interviewDataRef?.current) return;
      if (interviewDataRef.current.datesSet?.startStr) return;
      const timeZone = moment.tz.guess();
      const weekStart = moment().tz(timeZone).startOf('week');
      const weekEnd = weekStart.clone().add(2, 'weeks');
      updateSharedData({
        ...interviewDataRef.current,
        datesSet: {
          startStr: weekStart.format('YYYY-MM-DD'),
          endStr: weekEnd.format('YYYY-MM-DD'),
          timeZone,
        },
      });
    }, [formOnly, updateSharedData, interviewDataRef]);

    useEffect(() => {
      if (!interviewPlan) return;
      let initialSchedulerState: SchedulerDetailsTypes.SchedulerState = {};
      if (
        isEdit &&
        (variant === 'self' || variant === 'bulk-self') &&
        interviewPlan?.time_slots
      ) {
        const initialTimezone = interviewPlan.time_slots[0]?.start_time
          ? moment.parseZone(interviewPlan.time_slots[0].start_time).tz() ||
            moment.tz.guess()
          : moment.tz.guess();
        initialSchedulerState = initializeSelfScheduleTimeSlots(
          interviewPlan.time_slots.map((slot) => ({
            start_time: slot.start_time,
            end_time: slot.end_time,
            is_custom: slot.is_custom,
            override_type: slot.override_type,
          })),
          initialTimezone,
          dateFormat
        );
      }

      const isUnSpecifiedLocation =
        (interviewPlan?.kind || interviewPlan?.interview_kind) === 'unmanaged';

      let interviewKind =
        interviewPlan?.kind ||
        interviewPlan?.interview_kind ||
        (isAdhoc ? 'one_on_one' : 'panel');

      if (isUnSpecifiedLocation) {
        interviewKind =
          interviewPlan?.participants?.length > 1 ? 'panel' : 'one_on_one';
      }

      const interviewName =
        interviewPlan?.substituted_name || interviewPlan?.name || '';

      // Only overwrite schedulerState when loading edit-mode time_slots; else preserve (fix: slots deselect on re-render).
      const isEditSelfWithTimeSlots =
        isEdit &&
        (variant === 'self' || variant === 'bulk-self') &&
        interviewPlan?.time_slots?.length > 0;
      const schedulerStateToSet = isEditSelfWithTimeSlots
        ? initialSchedulerState
        : (interviewDataRef.current?.schedulerState ?? initialSchedulerState);

      const sharedCalendarEnabled = !!interviewPlan?.shared_calendar_enabled;
      updateSharedData({
        ...interviewDataRef.current,
        interviewName,
        interviewKind,
        interviewLocation:
          !interviewPlan?.location || interviewPlan?.location === 'unspecified'
            ? undefined
            : interviewPlan?.location,
        userEventsInfo: persistedUserEventsInfo,
        roomEventsInfo: persistedRoomEventsInfo,
        ...(sharedCalendarEnabled
          ? {
              calendar_info:
                interviewPlan?.calendar_info ||
                interviewDataRef.current.calendar_info,
            }
          : {}),
        bookedSlot: {
          ...interviewDataRef.current.bookedSlot,
          end: interviewPlan?.end_time,
          start: interviewPlan?.start_time,
          extendedProps: {
            ...interviewDataRef.current.bookedSlot.extendedProps,
            isOriginalSlotModified: interviewPlan?.isOriginalSlotModified,
            interviewerStatus: interviewPlan?.interviewer_status,
          },
          duration:
            variant === 'manual'
              ? getDurationFromTemplate(interviewPlan)
              : interviewPlan?.duration || SchedulerDetailsConstants.DURATION,
        },
        schedulerState: schedulerStateToSet,
        candidate,
        minimum_notice_config: interviewPlan?.minimum_notice_config,
        minimum_notice_enabled: interviewPlan?.minimum_notice_enabled,
        fill_with_ai_enabled: interviewPlan?.fill_with_ai_enabled,
      });
      if (!sharedCalendarEnabled) {
        delete interviewDataRef.current.calendar_info;
      }
    }, [
      interviewPlan,
      isEdit,
      variant,
      updateSharedData,
      dateFormat,
      candidate,
      interviewDataRef,
      getDurationFromTemplate,
      initializeSelfScheduleTimeSlots,
      isAdhoc,
      persistedUserEventsInfo,
      persistedRoomEventsInfo,
    ]);

    useEffect(() => {
      if (calendarRef.current && interviewPlan?.start_time) {
        const calendarApi = calendarRef.current.getApi();
        const date = new Date(interviewPlan?.start_time);
        calendarApi.gotoDate(date);
      }
    }, [calendarRef, interviewPlan]);

    useImperativeHandle(ref, () => ({
      getModel: () => getSchedularDetailsModel(),
      getFormRef: () => formRef,
      getUpdateSharedData: () => updateSharedData,
    }));

    const commonProps = {
      interviewDataRef,
      updateSharedData,
      registerForUpdates,
      variant,
      isEdit,
      jobId,
      jobStageActivityId,
      calendarRef,
      formRef,
      interviewPlan,
      isAdhoc,
      isBulkManual,
      applicationData,
      timezone,
      currentTemplateData,
      updateTemplateModificationData,

      customForm,
      interviewPlatform,
      showScorecardStep,
    };

    const renderBulkManualSteps = () => {
      const canNavigateToStep = (targetIndex: number) => {
        // Always allow clicking on current step
        if (targetIndex === currentInterviewIndex) return false;

        // In manual scheduling mode, only allow navigation to previous steps
        // and the immediate next step (if current step is completed)
        if (isManualScheduling) {
          // Allow navigation to any previous step
          if (targetIndex < currentInterviewIndex) return true;

          // Only allow navigation to the immediate next step
          if (targetIndex === currentInterviewIndex + 1) return true;

          // Block jumping ahead to future steps
          return false;
        }

        // In regular mode, allow navigation to any step
        return true;
      };

      const getStepCursor = (index: number) => {
        if (currentInterviewIndex === index) return 'not-allowed';
        if (!canNavigateToStep(index)) return 'not-allowed';
        return 'pointer';
      };

      const getStepColor = (index: number) => {
        if (currentInterviewIndex === index) return 'primary-500';

        // In manual scheduling, show different colors for accessible vs inaccessible steps
        if (
          isManualScheduling &&
          !canNavigateToStep(index) &&
          index > currentInterviewIndex
        ) {
          return 'primary-100';
        }

        return 'primary-300';
      };

      return (
        <Flex
          w="100%"
          justifyContent="center"
          alignItems="center"
          gap="12px"
          minH="61px"
          borderBottom="1px solid"
          borderColor="ats-border-color"
        >
          {interviewConfigs?.map((_, index) => (
            <Box
              w="20px"
              h="20px"
              display="flex"
              cursor={getStepCursor(index)}
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              onClick={() => {
                if (canNavigateToStep(index)) {
                  if (updateInterviewDataCallback) {
                    updateInterviewDataCallback({
                      data: {
                        ...interviewDataRef.current,
                      },
                      currentIndex: currentInterviewIndex,
                      newIndex: index,
                    });
                  }
                }
              }}
              backgroundColor={getStepColor(index)}
              key={index}
            >
              <KText variant="heading_3" color="white">
                {index + 1}
              </KText>
            </Box>
          ))}
        </Flex>
      );
    };

    return (
      <>
        <Flex w="100%" h="100%" flex={1} overflow="hidden" position="relative">
          {!formOnly && (
            <Box h="100%" w="70%" borderColor="border-color">
              {(variant === 'manual' || variant === 'bulk-manual') && (
                <InterviewManualScheduler {...commonProps} />
              )}
              {(variant === 'self' || variant === 'bulk-self') && (
                <InterviewSelfScheduler {...commonProps} />
              )}
            </Box>
          )}
          <Flex
            h="100%"
            flexDir="column"
            w={formOnly ? '100%' : '30%'}
            maxW={formOnly ? '100%' : '30%'}
            minW={formOnly ? undefined : '380px'}
            alignItems={formOnly ? 'center' : undefined}
          >
            {isBulkManual && renderBulkManualSteps()}
            <Box
              h="100%"
              px="xxl"
              py="sm"
              w="100%"
              overflowY="auto"
              overflowX="hidden"
            >
              <Box
                h="100%"
                maxW={formOnly ? '500px' : undefined}
                mx={formOnly ? 'auto' : undefined}
              >
                <InterviewSchedulerForm
                  key={jobStageActivityId}
                  {...commonProps}
                  variant={variant === 'bulk-self' ? 'self' : variant}
                />
              </Box>
            </Box>
          </Flex>
        </Flex>
        {isBulkManual && (
          <SchedulerDetailsFooter
            interviewPlan={interviewPlan}
            previewPayload={previewPayload}
            previewDetails={getPreviewDetails()}
            interviewDataRef={interviewDataRef}
            navigationConfig={navigationConfig}
            updateSharedData={updateSharedData}
            registerForUpdates={registerForUpdates}
            currentInterviewIndex={currentInterviewIndex}
            updateInterviewDataCallback={updateInterviewDataCallback}
            currentTemplateData={currentTemplateData}
            updateTemplateModificationData={updateTemplateModificationData}
            getSchedularDetailsModel={getSchedularDetailsModel}
          />
        )}
      </>
    );
  })
);
